import { useEffect } from "react";

/**
 * Set the browser tab title, meta description, and optional JSON-LD
 * structured data for a page.
 *
 * Two call signatures (both supported for backwards compatibility):
 *   1) Positional:  usePageMeta("Title", "Description")
 *   2) Object:      usePageMeta({ title, description, jsonLd, canonical })
 *
 * `jsonLd` can be a single object or an array of objects — one
 * <script type="application/ld+json"> tag is injected per object and
 * removed on unmount.
 */
export default function usePageMeta(arg1, arg2) {
  // Normalize the two call signatures into a single options object
  const opts = (arg1 && typeof arg1 === "object" && !Array.isArray(arg1))
    ? arg1
    : { title: arg1, description: arg2 };
  const { title, description, jsonLd, canonical } = opts;

  useEffect(() => {
    // --- Title ---
    const prevTitle = document.title;
    if (title) document.title = title;

    // --- Meta description ---
    const descTag = document.querySelector('meta[name="description"]');
    const prevDesc = descTag?.content;
    if (description && descTag) descTag.content = description;

    // --- Canonical link ---
    let canonicalTag = null;
    let prevCanonical = null;
    if (canonical) {
      canonicalTag = document.querySelector('link[rel="canonical"]');
      if (canonicalTag) {
        prevCanonical = canonicalTag.href;
        canonicalTag.href = canonical;
      } else {
        canonicalTag = document.createElement("link");
        canonicalTag.rel = "canonical";
        canonicalTag.href = canonical;
        canonicalTag.dataset.hcManaged = "1";
        document.head.appendChild(canonicalTag);
      }
    }

    // --- JSON-LD structured data ---
    const injectedScripts = [];
    if (jsonLd) {
      const blocks = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      blocks.forEach((obj) => {
        if (!obj) return;
        try {
          const script = document.createElement("script");
          script.type = "application/ld+json";
          script.dataset.hcManaged = "1";
          script.text = JSON.stringify(obj);
          document.head.appendChild(script);
          injectedScripts.push(script);
        } catch (e) {
          // Bad payload — skip silently in prod
        }
      });
    }

    return () => {
      document.title = prevTitle;
      if (descTag && prevDesc != null) descTag.content = prevDesc;
      if (canonicalTag) {
        if (canonicalTag.dataset.hcManaged === "1" && prevCanonical == null) {
          canonicalTag.remove();
        } else if (prevCanonical != null) {
          canonicalTag.href = prevCanonical;
        }
      }
      injectedScripts.forEach((s) => s.remove());
    };
  }, [title, description, canonical, JSON.stringify(jsonLd)]);
}
